import type {
  AcceptQuoteInput,
  ContainerDetailInput,
  ContainerInput,
  CreateJobInput,
  CreditHoldDecisionInput,
  Currency,
  DropPointInput,
  JobAction,
  JobAccessorialOverrideInput,
  JobCostResponsibility,
  JobDeadlinePressure,
  JobHoldReason,
  JobListQuery,
  JobPriceSource,
  JobReasonInput,
  RejectQuoteInput,
  LooseCargoDetailInput,
  LooseCargoResponsibility,
  Page,
  QuoteAcceptanceChannel,
  QuoteDecision,
  QuoteJobInput,
  RateBasis,
  ReleaseStatus,
  ReorderDropPointsInput,
  UpdateJobInput,
} from '@chukua/common';
import type { UploadView } from '../../shared/features/uploads/uploads.api';
import { api } from '../../shared/shared.api';
import { toListParams } from '../../shared/policies/list-params';

export interface JobView {
  id: string;
  number: string;
  customerId: string;
  jobTypeCode: string;
  originZoneCode: string | null;
  destinationZoneCode: string | null;
  requestedDate: string | null;
  customerDeliveryDeadline: string | null;
  status: string;
  heldFromStatus: string | null;
  /** `manual` (the `hold` action) or `credit` (gitaumoses4/chukua#37) — null off `on_hold`. */
  holdReason: JobHoldReason | null;
  /** The rate card had no price and nobody has priced it yet (gitaumoses4/chukua#35); `confirm` refuses it. */
  pricingRequired: boolean;
  source: string | null;
  contactName: string | null;
  contactPhone: string | null;
  requestedQuantity: number | null;
  /** Commercial terms (gitaumoses4/chukua#33). */
  agreedPrice: { amountMinor: number; currency: Currency } | null;
  rateBasis: RateBasis | null;
  priceSource: JobPriceSource | null;
  tollsPayer: JobCostResponsibility | null;
  weighbridgePayer: JobCostResponsibility | null;
  cancellationTerms: string | null;
  paymentTermsDaysOverride: number | null;
  poNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DeadlinePressureView {
  deadlinePressure: JobDeadlinePressure | undefined;
  daysUntilDeadline: number | null;
}

export interface JobListItem extends DeadlinePressureView {
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  jobTypeCode: string;
  jobTypeName: string;
  originZoneCode: string | null;
  originZoneName: string | null;
  destinationZoneCode: string | null;
  destinationZoneName: string | null;
  requestedDate: string | null;
  customerDeliveryDeadline: string | null;
  status: string;
  /** The readiness checklist's overall state (gitaumoses4/chukua#36) — a tag, not the item breakdown; open the job for that. */
  ready: boolean;
  /** The price agreed with the customer and the unit it is quoted per, null until one is set (gitaumoses4/chukua#55). */
  agreedPrice: { amountMinor: number; currency: Currency } | null;
  rateBasis: RateBasis | null;
  /** Both null when the job never had a quote (gitaumoses4/chukua#34); `quoteExpired` only considers a still-pending quote, same reading as the list's `quoteExpired` filter. */
  quoteValidUntil: string | null;
  quoteExpired: boolean;
  /** Set only while `on_hold` (gitaumoses4/chukua#40). */
  holdReason: JobHoldReason | null;
  heldReasonName: string | null;
  heldAt: string | null;
}

/** A job's quote, frozen at quote time (gitaumoses4/chukua#34) — `JobDetail.quote` is `null` while the job has never been quoted. */
export interface JobQuoteView {
  amount: { amountMinor: number; currency: Currency };
  rateBasis: RateBasis;
  priceSource: JobPriceSource;
  validUntil: string;
  expired: boolean;
  decision: QuoteDecision;
  decidedAt: string | null;
  acceptanceChannel: QuoteAcceptanceChannel | null;
  acceptedByName: string | null;
  rejectionReason: string | null;
}

/** One readiness item's recorded override, same shape as an `overrides` row. */
export interface ReadinessOverride {
  reason: string;
  approverId: string;
  requestedBy: string;
  occurredAt: string;
}

/** One gate before dispatch (PRD → Job intake → Gates before dispatch; gitaumoses4/chukua#36). */
export interface ReadinessItem {
  key: string;
  overrideKind: string;
  label: string;
  naturallyMet: boolean;
  met: boolean;
  overridden: boolean;
  override: ReadinessOverride | null;
}

export interface ReadinessChecklist {
  ready: boolean;
  items: ReadinessItem[];
}

/** `GET /override-approvers?q=` — who holds `override.approve`, for the readiness override picker. */
export interface OverrideApprover {
  id: string;
  name: string;
  email: string;
}

export interface JobDetail extends JobView, DeadlinePressureView {
  attachments: UploadView[];
  containers: JobContainer[];
  containerDetails: JobContainerDetails | null;
  looseCargoDetails: JobLooseCargoDetails | null;
  dropPoints: DropPoint[];
  customerName: string;
  jobTypeName: string;
  originZoneName: string | null;
  destinationZoneName: string | null;
  /** Exactly what `JobStateActions` may offer right now — never re-derived client-side. */
  availableActions: JobAction[];
  /** Who was acting when `confirm` flagged this job for credit hold (gitaumoses4/chukua#37) — for `JobCreditHoldPanel`'s self-exclusion button state. Null off a credit hold. */
  creditFlaggedByUserId: string | null;
  /** The customer's own terms, shown beside the job's: whether a PO is demanded, and the payment terms a blank override falls back to. */
  customerPoNumberRequired: boolean;
  customerPaymentTermsDays: number;
  /** The computed checklist of gates before dispatch (gitaumoses4/chukua#36) — shown, never blocking. */
  readiness: ReadinessChecklist;
  /** `null` while the job has never been quoted (gitaumoses4/chukua#34). */
  quote: JobQuoteView | null;
}

export interface JobContainer {
  id: string;
  jobId: string;
  number: string;
  sizeCode: string;
  typeCode: string;
  seal: string | null;
  grossWeightKg: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface JobContainerDetails {
  shippingLine: string | null;
  vessel: string | null;
  billOfLading: string | null;
  releaseOrder: string | null;
  releaseStatus: ReleaseStatus | null;
  pickupZoneCode: string | null;
  emptyReturnDepotZoneCode: string;
  emptyReturnFreeDaysDeadline: string;
  isTransit: boolean;
  isHazardous: boolean;
  bondReference: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface JobLooseCargoDetails {
  commodity: string;
  weightKg: number | null;
  volumeM3: number | null;
  packaging: string | null;
  handlingNotes: string | null;
  loadingResponsibility: LooseCargoResponsibility | null;
  offloadingResponsibility: LooseCargoResponsibility | null;
  customerProvidesLabour: boolean;
  specialHandlingRequirementCodes: string[];
  specialHandlingNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DropPoint {
  id: string;
  jobId: string;
  zoneCode: string;
  contactName: string;
  contactPhone: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export function listJobs(query: JobListQuery): Promise<Page<JobListItem>> {
  return api<Page<JobListItem>>(`/jobs?${toListParams(query)}`);
}

export function createJob(input: CreateJobInput): Promise<JobView> {
  return api<JobView>('/jobs', { method: 'POST', body: JSON.stringify(input) });
}

export function getJob(id: string): Promise<JobDetail> {
  return api<JobDetail>(`/jobs/${id}`);
}

export function updateJob(id: string, input: UpdateJobInput): Promise<JobView> {
  return api<JobView>(`/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function quoteJob(id: string, input: QuoteJobInput): Promise<void> {
  await api<void>(`/jobs/${id}/quote`, { method: 'POST', body: JSON.stringify(input) });
}

/** 200 with the job, not 204 like the other actions (gitaumoses4/chukua#37) — the caller needs to know whether it landed on Confirmed or On hold (credit). */
export function confirmJob(id: string): Promise<JobView> {
  return api<JobView>(`/jobs/${id}/confirm`, { method: 'POST' });
}

/** Same 200-with-the-job reasoning as `confirm` (gitaumoses4/chukua#34): the caller needs Confirmed vs. On hold (credit) to toast correctly. */
export function acceptQuote(id: string, input: AcceptQuoteInput): Promise<JobView> {
  return api<JobView>(`/jobs/${id}/accept-quote`, { method: 'POST', body: JSON.stringify(input) });
}

/** One certain outcome (quoted → cancelled), so void like every other single-outcome action. */
export async function rejectQuote(id: string, input: RejectQuoteInput): Promise<void> {
  await api<void>(`/jobs/${id}/reject-quote`, { method: 'POST', body: JSON.stringify(input) });
}

export async function decideCreditHold(id: string, input: CreditHoldDecisionInput): Promise<void> {
  await api<void>(`/jobs/${id}/credit-hold-decision`, { method: 'POST', body: JSON.stringify(input) });
}

export async function resumeJob(id: string): Promise<void> {
  await api<void>(`/jobs/${id}/resume`, { method: 'POST' });
}

export async function closeJob(id: string): Promise<void> {
  await api<void>(`/jobs/${id}/close`, { method: 'POST' });
}

export async function cancelJob(id: string, input: JobReasonInput): Promise<void> {
  await api<void>(`/jobs/${id}/cancel`, { method: 'POST', body: JSON.stringify(input) });
}

export async function holdJob(id: string, input: JobReasonInput): Promise<void> {
  await api<void>(`/jobs/${id}/hold`, { method: 'POST', body: JSON.stringify(input) });
}

export function addJobContainer(jobId: string, input: ContainerInput): Promise<JobContainer> {
  return api<JobContainer>(`/jobs/${jobId}/containers`, { method: 'POST', body: JSON.stringify(input) });
}

export function updateJobContainer(jobId: string, containerId: string, input: ContainerInput): Promise<JobContainer> {
  return api<JobContainer>(`/jobs/${jobId}/containers/${containerId}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function removeJobContainer(jobId: string, containerId: string): Promise<void> {
  await api<void>(`/jobs/${jobId}/containers/${containerId}`, { method: 'DELETE' });
}

export function saveJobContainerDetails(jobId: string, input: ContainerDetailInput): Promise<JobContainerDetails> {
  return api<JobContainerDetails>(`/jobs/${jobId}/container-details`, { method: 'PUT', body: JSON.stringify(input) });
}

export function saveJobLooseCargoDetails(jobId: string, input: LooseCargoDetailInput): Promise<JobLooseCargoDetails> {
  return api<JobLooseCargoDetails>(`/jobs/${jobId}/loose-cargo-details`, { method: 'PUT', body: JSON.stringify(input) });
}

export function listDropPoints(jobId: string): Promise<DropPoint[]> {
  return api<DropPoint[]>(`/jobs/${jobId}/drop-points`);
}

export function addDropPoint(jobId: string, input: DropPointInput): Promise<DropPoint> {
  return api<DropPoint>(`/jobs/${jobId}/drop-points`, { method: 'POST', body: JSON.stringify(input) });
}

export function updateDropPoint(jobId: string, dropPointId: string, input: DropPointInput): Promise<DropPoint> {
  return api<DropPoint>(`/jobs/${jobId}/drop-points/${dropPointId}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function removeDropPoint(jobId: string, dropPointId: string): Promise<void> {
  await api<void>(`/jobs/${jobId}/drop-points/${dropPointId}`, { method: 'DELETE' });
}

export function reorderDropPoints(jobId: string, input: ReorderDropPointsInput): Promise<DropPoint[]> {
  return api<DropPoint[]>(`/jobs/${jobId}/drop-points/order`, { method: 'PUT', body: JSON.stringify(input) });
}

/** A job's free hours/days per accessorial type: what was copied at intake and what applies now (gitaumoses4/chukua#33). */
export interface JobAccessorialTerm {
  accessorialTypeCode: string;
  accessorialTypeName: string;
  unit: 'per_hour' | 'per_day' | 'per_event';
  originalFreeQuantity: number | null;
  effectiveFreeQuantity: number | null;
  isOverridden: boolean;
}

export function listJobAccessorialTerms(jobId: string): Promise<JobAccessorialTerm[]> {
  return api(`/jobs/${jobId}/accessorial-terms`);
}

export function overrideJobAccessorialFreeQuantity(
  jobId: string,
  accessorialTypeCode: string,
  input: JobAccessorialOverrideInput,
): Promise<JobAccessorialTerm[]> {
  return api(`/jobs/${jobId}/accessorial-terms/${accessorialTypeCode}/override`, { method: 'POST', body: JSON.stringify(input) });
}

/** Tags an already-confirmed upload as satisfying one of this job type's required documents (gitaumoses4/chukua#36). */
export async function attachRequiredDocument(jobId: string, requiredDocumentTypeId: string, uploadId: string): Promise<void> {
  await api<void>(`/jobs/${jobId}/readiness/required-documents/${requiredDocumentTypeId}/attachments`, { method: 'POST', body: JSON.stringify({ uploadId }) });
}

export interface RecordReadinessOverrideInput {
  itemKey: string;
  approverId: string;
  reason: string;
}

export async function recordReadinessOverride(jobId: string, input: RecordReadinessOverrideInput): Promise<void> {
  await api<void>(`/jobs/${jobId}/readiness/override`, { method: 'POST', body: JSON.stringify(input) });
}

/** Who holds `override.approve` — a search-select picker for the readiness override's approver, session-only on the API side (no `override.approve` needed to search). */
export function searchOverrideApprovers(q: string): Promise<OverrideApprover[]> {
  return api<OverrideApprover[]>(`/override-approvers?q=${encodeURIComponent(q)}`);
}
